import { SalesOrganization } from "../components/Header";
import { Language, isNewShop, languageToCode } from "./languageUtils";

export type EnvironmentsType = "dev" | "test" | "acc" | "prod";

const environments: { [key in EnvironmentsType]: string } = {
  dev: "dev.",
  test: "dev.",
  acc: "test.",
  prod: "",
};

type ShopNames = "nl" | "be" | "lu" | "fr" | "ch" | "uk" | "de";

const shopNames: { [key in ShopNames]: string } = {
  nl: "shop.eriks.nl/",
  be: "shop.eriks.be/",
  lu: "shop.eriks.lu/",
  fr: "shop.eriks.fr/",
  ch: "shop.maagtechnic.ch/",
  uk: "shop.eriks.co.uk/",
  de: "shop.eriks.de/",
};

const oRingUrlPerLanguage: { [key in string]: string } = {
  nl: "afdichtingstechniek-o-ringen-en-toebehoren-o-ringen/",
  fr:
    "technologie-d-etancheite-joints-toriques-o-rings-et-accessoires-joint-torique-o-rings/",
  en: "sealing-equipment-o-rings-and-accessories-o-rings/",
  de: "dichtungstechnik-o-ringe-und-zubehoer-o-ringe/",
};

export const constructUrl = (
  environment: EnvironmentsType = "test",
  erpSystem: ShopNames = "nl",
  language: string = "nl"
) => {
  return `https://${environments[environment]}${shopNames[erpSystem]}${language}/${oRingUrlPerLanguage[language]}`;
};

export const getShopBaseUrl = (url: string): string => {
  try {
    const { protocol, hostname } = new URL(url);
    return `${protocol}//${hostname}`;
  } catch {
    return "";
  }
};

// The new webshop login URL is country scoped (/nl/en/login?...), so the
// country in that URL wins over the erp system of the selector.
const getCountryCode = (loginUrl: string, erpSystem: string): string => {
  try {
    const [firstSegment] = new URL(loginUrl).pathname
      .split("/")
      .filter(Boolean);
    const country = /^[a-z]{2}$/i.test(firstSegment || "")
      ? firstSegment
      : erpSystem;
    return country.toLowerCase();
  } catch {
    return erpSystem.toLowerCase();
  }
};

/**
 * Builds the webshop link for an item based on the base URL the User API
 * returns in `loginUrl`:
 * - new webshop: https://domain.com/{country}/{language}/id/{erpPartNumber}
 * - old webshop: https://shop.eriks.xx/{language}/erpnumber/{erpPartNumber}
 * Falls back to the link coming from the items API when the login URL or the
 * part number is not available.
 */
export const constructItemLink = ({
  loginUrl,
  erpPartNumber,
  language,
  erpSystem,
  fallbackLink = "",
}: {
  loginUrl: string | null | undefined;
  erpPartNumber: string | null | undefined;
  language: string;
  erpSystem: string;
  fallbackLink?: string;
}): string => {
  if (!loginUrl || !erpPartNumber) return fallbackLink;
  const baseUrl = getShopBaseUrl(loginUrl);
  if (!baseUrl) return fallbackLink;

  const languageCode = (language || "en").toLowerCase();

  if (isNewShop(loginUrl)) {
    const country = getCountryCode(loginUrl, erpSystem);
    return `${baseUrl}/${country}/${languageCode}/id/${erpPartNumber}`;
  }

  return `${baseUrl}/${languageCode}/erpnumber/${erpPartNumber}`;
};

// The new webshop (eriks.com) serves the cart on /{country}/{language}/cart,
// the old HCL webshop on the ShoppingCartDisplay servlet of its sales organization shop url.
export const constructShoppingUrl = (
  data: SalesOrganization[] | null | undefined,
  language: Language,
  erpSystem: string,
  loginUrl?: string
) => {
  if (loginUrl && isNewShop(loginUrl)) {
    const baseUrl = getShopBaseUrl(loginUrl);
    if (!baseUrl || !erpSystem || !language) return "/";
    return `${baseUrl}/${erpSystem.toLowerCase()}/${language}/cart`;
  }

  if (!data || !Array.isArray(data) || data.length === 0 || !erpSystem)
    return "/";
  const salesOrganization = data.find(
    (org) => org.country === erpSystem.toUpperCase()
  );
  if (!salesOrganization) return "/";
  return `${
    salesOrganization.shop_url
  }ShoppingCartDisplay?catalogId=1000&langId=${languageToCode(
    language
  )}&storeId=${salesOrganization.store_id}`;
};
